// Generated by GitHub Copilot
// US1794533: Application Name on Impact Report Form
// Service for workload/component data operations

import { logError, getUserFriendlyErrorMessage } from '../utils/errorLogging';
import { authFetch } from '../utils/authFetch';

const API_BASE_URL = "/api/v1";

/**
 * Workload Service
 * Provides methods for searching and retrieving workload/component data
 */
class WorkloadService {
  /**
   * Search for workloads by name
   * @param {string} searchTerm - Search term to match against workload names
   * @param {number} limit - Maximum number of results (default: 50, max: 100)
   * @returns {Promise<Array>} Array of matching workload components
   */
  async searchWorkloadsByName(searchTerm, limit = 50) {
    if (!searchTerm || searchTerm.trim() === "") {
      return [];
    }

    try {
      const params = new URLSearchParams({
        q: searchTerm,
        limit: Math.min(limit, 100).toString(),
      });

      const response = await authFetch(
        `${API_BASE_URL}/workloads/search?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized - please log in");
        }
        if (response.status === 403) {
          throw new Error("Forbidden - insufficient permissions");
        }
        throw new Error(`Search failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.workloads || [];
    } catch (error) {
      logError('workloadService.searchWorkloadsByName', error, { searchTerm, limit });
      throw new Error(getUserFriendlyErrorMessage(error));
    }
  }

  /**
   * Get all workload components (requires configuration privilege)
   * @returns {Promise<Array>} Array of all workload components
   */
  async getAllWorkloads() {
    try {
      const response = await authFetch(`${API_BASE_URL}/workloads/components`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch workloads: ${response.statusText}`);
      }

      const data = await response.json();
      return data.components || [];
    } catch (error) {
      logError('workloadService.getAllWorkloads', error);
      throw new Error(getUserFriendlyErrorMessage(error));
    }
  }

  /**
   * Get workload component count (requires configuration privilege)
   * @returns {Promise<number>} Total count of workload components
   */
  async getWorkloadCount() {
    try {
      const response = await authFetch(
        `${API_BASE_URL}/workloads/components/count`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch workload count: ${response.statusText}`
        );
      }

      const data = await response.json();
      return data.count || 0;
    } catch (error) {
      logError('workloadService.getWorkloadCount', error);
      throw new Error(getUserFriendlyErrorMessage(error));
    }
  }

  /**
   * Lookup workload by CI ID
   * US1794540: Allow User to enter CI value on Impact Form
   * @param {string} ciID - CI ID to lookup
   * @returns {Promise<Object|null>} Workload component or null if not found
   */
  async lookupWorkloadByCIID(ciID) {
    if (!ciID || ciID.trim() === "") {
      return null;
    }

    try {
      const params = new URLSearchParams({
        ci: ciID.trim(),
      });

      const response = await authFetch(
        `${API_BASE_URL}/workloads/lookup?${params}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Include cookies for authentication
        }
      );

      // Handle 404 as "not found" - not an error
      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error("Unauthorized - please log in");
        }
        if (response.status === 403) {
          throw new Error("Forbidden - insufficient permissions");
        }
        throw new Error(`Lookup failed: ${response.statusText}`);
      }

      const data = await response.json();
      return data.workload || null;
    } catch (error) {
      logError('workloadService.lookupWorkloadByCIID', error, { ciID });
      throw new Error(getUserFriendlyErrorMessage(error));
    }
  }
}

// Export singleton instance
export const workloadService = new WorkloadService();
export default workloadService;
